import React, { useEffect, useState } from 'react';
import { Form, redirect, useNavigation } from 'react-router-dom';
import { useDashboardContext } from '@pages/DashboardLayout';
import { toast } from 'react-toastify';
import customFetch from '@utils/customFetch';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import SettingsNav from './SettingsNav';
import AddressFields, {
  readAddressFields,
} from '@components/shared/AddressFields';

const addressQuery = {
  queryKey: ['address'],
  queryFn: async () => {
    const data = await customFetch.get('/address');
    return data;
  },
};

export const loader = (queryClient) => async () => {
  const { data } = await queryClient.ensureQueryData(addressQuery);
  return data;
};

export const action =
  (queryClient) =>
  async ({ request }) => {
    const formData = await request.formData();
    // Switch für Forms
    const intent = formData.get('intent');

    if (intent === 'allgemeine_einstellungen') {
      const file = formData.get('avatar');

      if (file && file.size > 10000000) {
        toast.error('Das Bild darf nicht grösser als 10MB sein');
        return null;
      }

      try {
        await customFetch.patch('/users/update-user', formData);
        queryClient.invalidateQueries(['user']);
        toast.success('Erfolgreich angepasst!');
        return redirect('/dashboard/einstellungen');
      } catch (error) {
        toast.error(error?.response?.data?.msg);
        return error;
      }
    } else if (intent === 'lieferadresse' || intent === 'rechnungsadresse') {
      const data = Object.fromEntries(formData);
      try {
        if (intent === 'lieferadresse') {
          await customFetch.put(
            '/address/shipping',
            readAddressFields(data, 'shipping')
          );
        } else if (data.billingSameAsShipping === 'on') {
          await customFetch.delete('/address/billing');
        } else {
          await customFetch.put(
            '/address/billing',
            readAddressFields(data, 'billing')
          );
        }
        toast.success('Adresse gespeichert!');
        queryClient.invalidateQueries(['address']);
        return redirect('/dashboard/einstellungen');
      } catch (error) {
        toast.error(error?.response?.data?.msg);
        return error;
      }
    }
  };

const Settings = () => {
  const { user } = useDashboardContext();

  // E-Mail-Wechsel-Dialog (Double-Opt-in; bei aktivem Email-OTP zusätzlich Code)
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailOtp, setEmailOtp] = useState('');
  const [otpRequired, setOtpRequired] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  const { shippingAddress, billingAddress } = useQuery(addressQuery).data.data;
  // Ohne gespeicherte Rechnungsadresse gilt die Lieferadresse.
  const [billingSameAsShipping, setBillingSameAsShipping] = useState(
    !billingAddress
  );

  // Image Preview
  const [selectedFile, setSelectedFile] = useState();
  const [preview, setPreview] = useState();
  // create a preview as a side effect, whenever selected file is changed
  useEffect(() => {
    if (!selectedFile) {
      setPreview(undefined);
      return;
    }

    const objectUrl = URL.createObjectURL(selectedFile);
    setPreview(objectUrl);

    // free memory when ever this component is unmounted
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const onSelectFile = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      setSelectedFile(undefined);
      return;
    }

    // I've kept this example simple by using the first image instead of multiple
    setSelectedFile(e.target.files[0]);
  };

  const navigation = useNavigation();
  const isSubmitting = navigation.state === 'submitting';

  const closeEmailDialog = () => {
    setEmailDialogOpen(false);
    setNewEmail('');
    setEmailPassword('');
    setEmailOtp('');
    setOtpRequired(false);
  };

  // E-Mail-Wechsel anfordern. Bei aktivem Email-OTP verlangt das Backend
  // zunächst einen Code an die aktuelle Adresse (otpRequired), erst danach
  // wird der Bestätigungslink an die neue Adresse versendet.
  const handleRequestEmailChange = async (event) => {
    event.preventDefault();
    setEmailBusy(true);
    try {
      const { data } = await customFetch.post(
        '/auth/account/email-change/request',
        {
          password: emailPassword,
          newEmail: newEmail.trim(),
          ...(emailOtp ? { otp: emailOtp.trim() } : {}),
        }
      );
      if (data.otpRequired) {
        setOtpRequired(true);
        toast.info(data.msg);
      } else {
        toast.success(data.msg);
        closeEmailDialog();
      }
    } catch (error) {
      toast.error(error?.response?.data?.msg || 'Etwas ist schiefgelaufen');
    } finally {
      setEmailBusy(false);
    }
  };

  return (
    <main>
      <SettingsNav current="Account" />

      {/* Settings forms */}
      <div className="divide-y divide-gray/5">
        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Persönliche Informationen
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              Name, Titel und E-Mail-Adresse verwalten. Die Felder "Vorname",
              "Name", "Titel / Jobbezeichnung" sowie "Unternehmen" erscheinen
              als fixe Felder auf deiner Profilseite.
            </p>
          </div>

          <Form
            method="POST"
            className="md:col-span-2"
            encType="multipart/form-data"
          >
            <div className="grid grid-cols-1 gap-x-6 gap-y-8 sm:max-w-xl sm:grid-cols-6">
              <div className="col-span-full flex items-center gap-x-8">
                {selectedFile ? (
                  <img
                    src={preview}
                    alt="avatar"
                    className="h-24 w-24 flex-none rounded-lg bg-gray-800 object-cover"
                  />
                ) : (
                  <img
                    src={user.avatarUrl || user.avatar}
                    alt="avatar"
                    className="h-24 w-24 flex-none rounded-lg bg-gray-800 object-cover"
                  />
                )}

                <div>
                  <button
                    type="button"
                    className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs hover:bg-gray-50 ring-1 ring-inset ring-gray-300"
                  >
                    <label htmlFor="avatar" className="relative cursor-pointer">
                      Profilbild wechseln
                    </label>
                    <input
                      id="avatar"
                      name="avatar"
                      type="file"
                      accept=".png, .jpg, .jpeg"
                      className="sr-only"
                      onChange={onSelectFile}
                    />
                  </button>
                  <p className="mt-2 text-xs leading-5 text-gray-700">
                    JPG oder PNG. 10MB max.
                  </p>
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Vorname
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="firstName"
                    id="firstName"
                    autoComplete="given-name"
                    className="capitalize block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300  focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.firstName}
                  />
                </div>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Nachname
                </label>
                <div className="mt-2">
                  <input
                    type="text"
                    name="lastName"
                    id="lastName"
                    autoComplete="family-name"
                    className="capitalize block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.lastName}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="title"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Titel / Jobbezeichnung
                </label>
                <div className="mt-2">
                  <input
                    id="title"
                    name="title"
                    type="text"
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    defaultValue={user.title}
                  />
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="organization"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Unternehmen
                </label>
                <div className="mt-2">
                  <input
                    id="organization"
                    name="organization"
                    type="text"
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-500 disabled:ring-gray-200 sm:text-sm sm:leading-6"
                    defaultValue={
                      user.companyHasSubscription
                        ? user.companyName
                        : user.organization || ''
                    }
                    disabled={!!user.companyHasSubscription}
                  />
                  {!!user.companyHasSubscription && (
                    <p className="mt-1 text-xs text-gray-500">
                      Wird durch deine Firmenzugehörigkeit bestimmt.
                    </p>
                  )}
                </div>
              </div>

              <div className="col-span-full">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  E-Mail-Adresse
                </label>
                <div className="mt-2 flex items-center gap-x-3">
                  <input
                    id="email"
                    type="email"
                    readOnly
                    disabled
                    className="block w-full rounded-md border-0 bg-gray-100 px-3 py-1.5 text-gray-500 shadow-xs ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
                    value={user.email}
                  />
                  <button
                    type="button"
                    onClick={() => setEmailDialogOpen(true)}
                    className="shrink-0 rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                  >
                    Ändern
                  </button>
                </div>
                <p className="mt-2 text-xs leading-5 text-gray-500">
                  Aus Sicherheitsgründen wird die Änderung per Bestätigungslink
                  an die neue Adresse abgeschlossen.
                </p>
              </div>
            </div>

            <div className="mt-8 flex">
              <button
                type="submit"
                name="intent"
                value="allgemeine_einstellungen"
                className="rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </Form>
        </div>

        {/* Lieferadresse */}

        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Lieferadresse
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              Wohin deine Bestellungen verschickt werden. Der Checkout füllt sie
              automatisch aus.
            </p>
          </div>

          <Form method="POST" className="md:col-span-2">
            <AddressFields
              section="shipping"
              variant="settings"
              defaults={shippingAddress}
              required={['firstName', 'lastName', 'address', 'city', 'zip']}
            />

            <div className="mt-8 flex">
              <button
                type="submit"
                name="intent"
                value="lieferadresse"
                className="rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </Form>
        </div>

        {/* Rechnungsadresse */}

        <div className="grid max-w-7xl grid-cols-1 gap-x-8 gap-y-10 px-4 py-16 sm:px-6 md:grid-cols-3 lg:px-8">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">
              Rechnungsadresse
            </h2>
            <p className="mt-1 text-sm leading-6 text-gray-700">
              Nur nötig, wenn die Rechnung an eine andere Adresse gehen soll
              als die Lieferung.
            </p>
          </div>

          <Form method="POST" className="md:col-span-2">
            <div className="mb-8 flex items-center gap-3">
              <input
                id="billingSameAsShipping"
                name="billingSameAsShipping"
                type="checkbox"
                checked={billingSameAsShipping}
                onChange={(e) => setBillingSameAsShipping(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-svisit-primary-600 focus:ring-svisit-primary-500"
              />
              <label
                htmlFor="billingSameAsShipping"
                className="text-sm leading-6 text-gray-900"
              >
                Gleich wie Lieferadresse
              </label>
            </div>

            {!billingSameAsShipping && (
              <AddressFields
                section="billing"
                variant="settings"
                defaults={billingAddress}
                required={['address', 'city', 'zip']}
              />
            )}

            <div className="mt-8 flex">
              <button
                type="submit"
                name="intent"
                value="rechnungsadresse"
                className="rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-svisit-primary-500"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Speichert...' : 'Speichern'}
              </button>
            </div>
          </Form>
        </div>

      </div>

      {/* E-Mail-Wechsel-Dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={closeEmailDialog}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-gray-900/50" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <DialogTitle className="text-base font-semibold leading-7 text-gray-900">
              E-Mail-Adresse ändern
            </DialogTitle>
            <form onSubmit={handleRequestEmailChange} className="mt-4 space-y-4">
              <div>
                <label
                  htmlFor="new-email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Neue E-Mail-Adresse
                </label>
                <div className="mt-2">
                  <input
                    id="new-email"
                    type="email"
                    required
                    autoFocus
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div>
                <label
                  htmlFor="email-change-password"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Bestätige mit deinem Passwort
                </label>
                <div className="mt-2">
                  <input
                    id="email-change-password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={emailPassword}
                    onChange={(e) => setEmailPassword(e.target.value)}
                    className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              {otpRequired && (
                <div>
                  <label
                    htmlFor="email-change-otp"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Code an deine aktuelle Adresse
                  </label>
                  <div className="mt-2">
                    <input
                      id="email-change-otp"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      required
                      value={emailOtp}
                      onChange={(e) => setEmailOtp(e.target.value)}
                      placeholder="123456"
                      className="block w-full rounded-md border-0 bg-white/5 px-3 py-1.5 text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-svisit-primary-500 sm:text-sm sm:leading-6"
                    />
                  </div>
                  <p className="mt-2 text-xs leading-5 text-gray-500">
                    Dein Account nutzt E-Mail-Bestätigung. Wir haben dir einen
                    Code an deine aktuelle Adresse gesendet.
                  </p>
                </div>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  disabled={emailBusy}
                  className="rounded-md bg-svisit-primary-500 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-svisit-primary-400 disabled:cursor-not-allowed disabled:bg-gray-300"
                >
                  {emailBusy
                    ? 'Sendet…'
                    : otpRequired
                    ? 'Wechsel bestätigen'
                    : 'Bestätigungslink senden'}
                </button>
                <button
                  type="button"
                  onClick={closeEmailDialog}
                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </DialogPanel>
        </div>
      </Dialog>
    </main>
  );
};

export default Settings;
